import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, Loader, Sparkles } from "lucide-react";
import ContactService from "../../services/common/contactService";
import toastHelper from "../../utilities/toastHelper";

export const ContactSection = () => {
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !email || !message) {
      toastHelper.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await ContactService.submitContact({
        name: `${firstName} ${lastName}`,
        email,
        subject: company || "Contact Request",
        message,
      });
      toastHelper.success("Message sent successfully! Our export specialists will respond promptly.");
      setFirstName("");
      setLastName("");
      setEmail("");
      setCompany("");
      setMessage("");
    } catch (error) {
      toastHelper.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-slate-50/70 py-20 border-b border-slate-200 text-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-3 mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 border border-amber-200 text-amber-800 text-xs font-bold uppercase">
            <Sparkles className="w-3.5 h-3.5 text-amber-600" />
            <span>Dedicated Trade Consultation</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Connect With Export Specialists
          </h2>
          <p className="text-sm text-slate-600 max-w-xl mx-auto">
            Have questions about custom features, ERP integrations, or deployment plans? Contact us today.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Form Card */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm">
            <h3 className="text-xl font-bold text-slate-900 mb-6">Send Us A Message</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label htmlFor="contactFirstName" className="block text-[11px] font-bold uppercase tracking-wider text-slate-700">
                    First Name *
                  </label>
                  <input
                    id="contactFirstName"
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50/80 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600 transition-all"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="contactLastName" className="block text-[11px] font-bold uppercase tracking-wider text-slate-700">
                    Last Name *
                  </label>
                  <input
                    id="contactLastName"
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50/80 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600 transition-all"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="contactEmail" className="block text-[11px] font-bold uppercase tracking-wider text-slate-700">
                  Email Address *
                </label>
                <input
                  id="contactEmail"
                  type="email"
                  placeholder="john@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50/80 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600 transition-all"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="contactCompany" className="block text-[11px] font-bold uppercase tracking-wider text-slate-700">
                  Company / Organization
                </label>
                <input
                  id="contactCompany"
                  type="text"
                  placeholder="Your company name"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50/80 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="contactMessage" className="block text-[11px] font-bold uppercase tracking-wider text-slate-700">
                  Message *
                </label>
                <textarea
                  id="contactMessage"
                  rows={4}
                  placeholder="Tell us about your export documentation & inventory requirements..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full p-4 bg-slate-50/80 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600 transition-all"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs rounded-xl shadow-md shadow-amber-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Sending Message...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Details Column */}
          <div className="space-y-5">
            <div className="bg-white border border-slate-200/90 p-6 rounded-2xl flex items-start gap-4 shadow-sm">
              <div className="p-3 bg-amber-100 border border-amber-200 rounded-xl text-amber-800 shrink-0">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-slate-900">Direct Phone</h4>
                <p className="text-xs text-slate-700 font-bold mt-0.5">+91 80000 07329</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Mon - Fri, 9:00 AM - 6:00 PM IST</p>
              </div>
            </div>

            <div className="bg-white border border-slate-200/90 p-6 rounded-2xl flex items-start gap-4 shadow-sm">
              <div className="p-3 bg-amber-100 border border-amber-200 rounded-xl text-amber-800 shrink-0">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-slate-900">Email Enquiries</h4>
                <p className="text-xs text-amber-700 font-bold mt-0.5">sales@exportstuff.com</p>
                <p className="text-xs text-slate-600 mt-0.5">support@exportstuff.com</p>
              </div>
            </div>

            <div className="bg-white border border-slate-200/90 p-6 rounded-2xl flex items-start gap-4 shadow-sm">
              <div className="p-3 bg-amber-100 border border-amber-200 rounded-xl text-amber-800 shrink-0">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-slate-900">Headquarters</h4>
                <p className="text-xs text-slate-700 leading-relaxed mt-0.5">
                  123 Business Park, Enterprise Zone<br />
                  Mumbai, Maharashtra 400001, India
                </p>
              </div>
            </div>

            <div className="bg-amber-500/10 border border-amber-200 p-6 rounded-2xl space-y-3">
              <h4 className="font-bold text-sm text-amber-800">Need an Interactive Demo?</h4>
              <p className="text-xs text-slate-700 leading-relaxed">
                Schedule a 1-on-1 walkthrough of the Quotation Document & Ceramic Tile module with our trade engineers.
              </p>
              <a
                href="#contact"
                className="inline-block px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all"
              >
                Request Custom Walkthrough
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
